import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ChainsService } from 'src/app/services/chains.service';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import {
  ReportFilterState,
  toApiParams,
} from 'src/app/shared/components/report-filter-bar/report-filter-bar.component';
import {
  DEFAULT_CHAIN_TILE_KEYS,
  MAX_TILES,
  NOGLETAL_GROUPS,
  STORE_KPI_CATALOG,
  TILE_ICONS,
} from 'src/app/shared/constants/kpi-catalog';
import { SavedView } from 'src/app/shared/models/dashboard-preferences.model';
import { KpiTilesConfigDialogComponent } from '../../stores/ecommerce-admin/store-overview/kpi-tiles-config-dialog/kpi-tiles-config-dialog.component';

interface KpiValue {
  value: number;
  format: string;
}

@Component({
  selector: 'app-chain-overview',
  templateUrl: './chain-overview.component.html',
  styleUrls: ['./chain-overview.component.scss'],
})
export class ChainOverviewComponent implements OnInit, OnDestroy {
  @ViewChild('trendChart') trendChart?: BaseChartDirective;

  chainId: string = '';
  chainName: string = '';
  role: string = localStorage.getItem('role') || '';

  filterState: ReportFilterState = { period: 'this_month' };
  loading = false;
  preferencesLoading = false;

  tiles: Record<string, KpiValue> = {};
  nogletal: Record<string, KpiValue> = {};
  dailyTrend: any[] = [];
  chainTileKeys: string[] = [...DEFAULT_CHAIN_TILE_KEYS];
  savedViews: SavedView[] = [];

  readonly nogletalGroups = NOGLETAL_GROUPS;
  readonly maxTiles = MAX_TILES;

  public trendChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Revenue (DKK)',
        backgroundColor: 'rgba(51, 102, 255, 0.15)',
        borderColor: 'rgba(51, 102, 255, 1)',
        fill: true,
        yAxisID: 'y',
      },
      {
        data: [],
        label: 'Orders',
        borderColor: 'rgba(40, 167, 69, 1)',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        fill: false,
        yAxisID: 'y1',
      },
    ],
  };

  public trendChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: { type: 'linear', display: true, position: 'left' },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: { drawOnChartArea: false },
      },
    },
    plugins: { legend: { display: true } },
  };

  public trendChartType: ChartType = 'line';

  private kpiLoadSeq = 0;
  private kpiRequestSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chainsService: ChainsService,
    private ecommerceService: EcommerceService,
    private dialogService: NbDialogService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.route.params.subscribe((params) => {
      this.chainId = params['chainId'];
    });
  }

  ngOnInit(): void {
    this.loadChainName();
    this.loadPreferences();
  }

  loadChainName(): void {
    if (!this.chainId) {
      return;
    }
    this.chainsService.getChain(this.chainId).subscribe({
      next: (res: any) => {
        const chain = res.chain || res.response?.data?.chain || res.response?.data;
        this.chainName = chain?.chainName || '';
      },
      error: (err: any) => {
        this.toastr.error(
          err.error?.response?.message ||
            this.translate.instant('KPI.CHAIN_OVERVIEW.LOAD_CHAIN_ERROR')
        );
      },
    });
  }

  onFilterChange(filter: ReportFilterState): void {
    this.filterState = filter;
    if (this.preferencesLoading) {
      return;
    }
    this.loadKpis();
  }

  onSavedViewApplied(filter: ReportFilterState): void {
    this.filterState = { ...filter };
    if (this.preferencesLoading) {
      return;
    }
    this.loadKpis();
  }

  onSavedViewsChange(views: SavedView[]): void {
    this.savedViews = views;
  }

  loadPreferences(): void {
    this.preferencesLoading = true;
    this.ecommerceService.getDashboardPreferences().subscribe({
      next: (res: any) => {
        const data = res.response?.data ?? res.data ?? res;
        if (Array.isArray(data?.chainTiles) && data.chainTiles.length) {
          this.chainTileKeys = data.chainTiles.slice(0, MAX_TILES);
        }
        if (Array.isArray(data?.savedViews)) {
          this.savedViews = data.savedViews;
        }
        this.preferencesLoading = false;
        if (this.chainId) {
          this.loadKpis();
        }
      },
      error: (err: any) => {
        this.preferencesLoading = false;
        if (this.chainId) {
          this.loadKpis();
        }
        this.toastr.warning(
          this.translate.instant('KPI.OVERVIEW.PREFERENCES_LOAD_ERROR')
        );
        console.warn('Failed to load dashboard preferences:', err);
      },
    });
  }

  loadKpis(): void {
    if (!this.chainId) {
      return;
    }

    const seq = ++this.kpiLoadSeq;
    this.loading = true;
    const params = this.buildKpiParams(this.filterState);

    this.kpiRequestSub?.unsubscribe();
    this.kpiRequestSub = this.ecommerceService
      .getChainKpis(this.chainId, params)
      .pipe(
        finalize(() => {
          if (seq === this.kpiLoadSeq) {
            this.loading = false;
          }
        })
      )
      .subscribe({
        next: (res: any) => {
          if (seq !== this.kpiLoadSeq) {
            return;
          }
          const data = res.response?.data ?? res.data ?? res;
          this.tiles = data.tiles || {};
          this.nogletal = data.nogletal || {};
          this.dailyTrend = data.dailyTrend || [];
          this.updateTrendChart();
        },
        error: (err: any) => {
          if (seq !== this.kpiLoadSeq) {
            return;
          }
          this.toastr.error(
            this.resolveKpiErrorMessage(err, 'KPI.CHAIN_OVERVIEW.LOAD_ERROR')
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.kpiRequestSub?.unsubscribe();
  }

  openConfigureDialog(): void {
    const dialogRef = this.dialogService.open(KpiTilesConfigDialogComponent, {
      context: {
        selectedKeys: [...this.chainTileKeys],
      },
      closeOnBackdropClick: false,
    });

    dialogRef.onClose.subscribe((result: string[] | undefined) => {
      if (!result) {
        return;
      }
      this.saveTilePreferences(result);
    });
  }

  saveTilePreferences(keys: string[]): void {
    this.ecommerceService.updateDashboardPreferences({ chainTiles: keys }).subscribe({
      next: (res: any) => {
        const data = res.response?.data ?? res.data ?? res;
        if (Array.isArray(data?.chainTiles)) {
          this.chainTileKeys = data.chainTiles.slice(0, MAX_TILES);
        } else {
          this.chainTileKeys = keys.slice(0, MAX_TILES);
        }
        this.toastr.success(this.translate.instant('KPI.CONFIGURE_TILES.SAVED'));
      },
      error: (err: any) => {
        this.toastr.error(
          err.error?.response?.message ||
            this.translate.instant('KPI.CONFIGURE_TILES.SAVE_ERROR')
        );
      },
    });
  }

  getTileValue(key: string): KpiValue | null {
    return this.tiles[key] || null;
  }

  getNogletalValue(key: string): KpiValue | null {
    return this.nogletal[key] || this.tiles[key] || null;
  }

  formatMetric(key: string, metric?: KpiValue | null): string {
    if (!metric) {
      return '—';
    }
    const format = metric.format || STORE_KPI_CATALOG[key]?.format || 'number';
    const value = metric.value ?? 0;

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('da-DK', {
          style: 'currency',
          currency: 'DKK',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      case 'decimal':
        return new Intl.NumberFormat('da-DK', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 2,
        }).format(value);
      case 'percent': {
        const sign = value > 0 ? '+' : '';
        return `${sign}${new Intl.NumberFormat('da-DK', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(value)}%`;
      }
      default:
        return new Intl.NumberFormat('da-DK', {
          maximumFractionDigits: 0,
        }).format(value);
    }
  }

  metricLabelKey(key: string): string {
    return `KPI.NOGLETAL.${this.camelToSnake(key)}`;
  }

  tileIcon(key: string): string {
    return TILE_ICONS[key] || 'bar-chart-outline';
  }

  get backLabel(): string {
    return this.role === 'chain_manager'
      ? this.translate.instant('KPI.CHAIN_OVERVIEW.BACK_MY_CHAINS')
      : this.translate.instant('KPI.CHAIN_OVERVIEW.BACK_CHAINS');
  }

  goBack(): void {
    if (this.role === 'chain_manager') {
      this.router.navigate(['/my-chains']);
    } else {
      this.router.navigate(['/chains']);
    }
  }

  private buildKpiParams(filter: ReportFilterState): Record<string, string> {
    const params = toApiParams(filter);
    return Object.fromEntries(
      Object.entries(params).map(([key, value]) => [key, String(value)])
    );
  }

  private updateTrendChart(): void {
    this.trendChartData.labels = this.dailyTrend.map((d) => d.date);
    this.trendChartData.datasets[0].data = this.dailyTrend.map((d) => d.revenue ?? 0);
    this.trendChartData.datasets[1].data = this.dailyTrend.map((d) => d.orderCount ?? 0);
    this.trendChart?.update();
  }

  private camelToSnake(key: string): string {
    return key.replace(/([A-Z])/g, '_$1').toUpperCase();
  }

  private resolveKpiErrorMessage(err: any, fallbackKey: string): string {
    const backendMsg = err?.error?.response?.message ?? err?.error?.message;
    if (typeof backendMsg === 'string' && backendMsg.trim()) {
      return backendMsg;
    }
    return this.translate.instant(fallbackKey);
  }
}
